import React, { useEffect } from 'react';
import { Box, Button, Divider, TextField, Typography } from '@mui/material';
import useApi from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';
import { useToast } from '../providers/toaster-provider';

interface ForgotPasswordProps {
    email: string;
    setEmail: (val: string) => void;
    onBack: () => void;
    step: number;
    setStep: (step: number) => void;
    socialLogin?: React.ReactNode;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ email, setEmail, onBack, step, setStep, socialLogin }) => {
    const [forgotPassword, setForgotPassword] = useApi();
    const { showError } = useToast();

    const handleSubmit = () => {
        if (!email) return;
        const { url, method } = buildApiRequestConfig(routes.user.forgotPassword);
        setForgotPassword({ url, method, body: { email: email.trim() } });
    };

    useEffect(() => {
        const { data, isLoading, error } = forgotPassword;
        if (data && !isLoading) {
            setStep(2);
        }
        if (error) {
            showError('Something went wrong. Please try again.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forgotPassword]);

    // Step 1: Enter email
    if (step === 1) {
        return (
        <>
            <Box mb={2} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={onBack}>
            <Typography fontSize={14}>&lt; Back to login</Typography>
            </Box>
            <Typography variant="h4" fontWeight={600} mb={1}>
                Forgot your password?
            </Typography>
            <Typography color="text.secondary" mb={3}>
                Don't worry, happens to all of us. Enter your email below to recover your password
            </Typography>
            <TextField
                label="Email"
                placeholder="john.doe@gmail.com"
                fullWidth
                margin="normal"
                value={email}
                onChange={e => setEmail(e.target.value)}
                InputProps={{ style: { background: 'var(--color-page-background)' } }}
            />
            <Button
                variant="contained"
                fullWidth
                disabled={!email || forgotPassword.isLoading}
                sx={{ bgcolor: 'var(--color-primary)', color: '#fff', fontWeight: 600, py: 1.2, borderRadius: 1, boxShadow: 'none', mb: 3, mt: 1 }}
                onClick={handleSubmit}
            >
                {forgotPassword.isLoading ? 'Submitting...' : 'Submit'}
            </Button>
            {socialLogin &&
                <>
                    <Divider sx={{ my: 2 }}>Or login with</Divider>
                    {socialLogin}
                </>
            }
        </>
        );
    }

    // Step 2: Check your email confirmation
    return (
        <>
            <Box mb={2} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={onBack}>
                <Typography fontSize={14} color="var(--color-primary)">&lt; Back to login</Typography>
            </Box>
            <Typography variant="h4" fontWeight={600} mb={1}>
                Check your email
            </Typography>
            <Typography color="text.secondary" mb={3}>
                If an account (or an eligible email) matches <strong>{email}</strong>, we've sent instructions on how to proceed. Please check your inbox.
            </Typography>
            <Button
                variant="contained"
                fullWidth
                sx={{ bgcolor: 'var(--color-primary)', color: '#fff', fontWeight: 600, py: 1.2, borderRadius: 1, boxShadow: 'none', mb: 3, mt: 1 }}
                onClick={onBack}
            >
                Back to login
            </Button>
        </>
    );
};

export default ForgotPassword;
